import React from 'react'
import PropTypes from 'prop-types'
import { formatISO } from 'date-fns'
import { IonDatetime, IonDatetimeButton, IonIcon, IonModal } from '@ionic/react'
import { calendarOutline } from 'ionicons/icons'
import { generateInputErrorId } from 'utils'
import InputError from 'components/formik/InputError'

// Shared "New ETA Date" + "New ETA Time" fields used by both the rescheduling
// and in-progress update partials. These intentionally use the section-specific
// markup (custom IonDatetime + native select) rather than the generic
// components/formik fields so the accept-decline section styling is preserved.
const TIME_OPTIONS = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
]

function EtaResetFields({
  idPrefix,
  values,
  errors,
  touched,
  setFieldValue,
  setFieldTouched,
  handleChange,
  handleBlur,
}) {
  const datetimeId = `${idPrefix}-eta-datetime`
  const timeSelectId = `${idPrefix}-eta-time`

  return (
    <>
      <div className="accept-decline-section__field">
        <label className="accept-decline-section__label" htmlFor={datetimeId}>
          <span className="required-indicator" aria-hidden="true">
            *
          </span>
          New ETA Date
        </label>
        <div className="accept-decline-section__datetime-wrapper">
          <IonDatetimeButton
            datetime={datetimeId}
            className="accept-decline-section__datetime-trigger"
            aria-required="true"
            aria-describedby={
              errors.eta_date && touched.eta_date
                ? generateInputErrorId('eta_date')
                : undefined
            }
          />
          <IonIcon
            icon={calendarOutline}
            className="accept-decline-section__datetime-icon"
            aria-hidden="true"
          />
          <IonModal keepContentsMounted={true}>
            <IonDatetime
              id={datetimeId}
              presentation="date"
              value={values.eta_date}
              showDefaultButtons={true}
              onIonChange={(e) => {
                const v = e.detail.value
                const dateValue = v
                  ? v.slice(0, 10)
                  : formatISO(new Date(), { representation: 'date' })
                setFieldValue('eta_date', dateValue)
                setFieldTouched('eta_date', true)
              }}
            />
          </IonModal>
        </div>
        <InputError
          name="eta_date"
          invalid={Boolean(errors.eta_date)}
          error={errors.eta_date}
          touched={touched.eta_date}
        />
      </div>

      <div className="accept-decline-section__field">
        <label className="accept-decline-section__label" htmlFor={timeSelectId}>
          <span className="required-indicator" aria-hidden="true">
            *
          </span>
          New ETA Time
        </label>
        <select
          id={timeSelectId}
          name="eta_time"
          className="accept-decline-section__input-native"
          value={values.eta_time}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-required="true"
          aria-describedby={
            errors.eta_time && touched.eta_time
              ? generateInputErrorId('eta_time')
              : undefined
          }
        >
          {TIME_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <InputError
          name="eta_time"
          invalid={Boolean(errors.eta_time)}
          error={errors.eta_time}
          touched={touched.eta_time}
        />
      </div>
    </>
  )
}

EtaResetFields.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
}

export default EtaResetFields
